import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * User-facing visibility flags for every UI surface Blueticks injects
 * into WhatsApp Web. The Display Settings panel
 * (`extension/context/content/components/blueticks-panel/sub-pages/
 * DisplaySettingsPage.tsx`) is the canonical editor.
 *
 * Each flag is a boolean — `true` means "show this feature in WA
 * Web", `false` means "hide it". A global stylesheet emitted by
 * `DisplayVisibilityStyles.tsx` (light DOM) plus a `ShadowFeatureGuard`
 * mounted inside every shadow-rooted React tree translate the off
 * state into `display: none !important` rules that target the
 * matching `data-bt-feature` wrapper. The imperative DOM walk
 * (also in DisplayVisibilityStyles) covers cases where CSS specificity
 * loses to MUI/WA inline styles.
 *
 * State is persisted to `localStorage` — extension-only override,
 * never written to the user record.
 */

const STORAGE_KEY = 'bt:display-settings';

/** Canonical catalog of every toggleable injected surface. Adding a
 *  feature: add the id here, tag its wrapper with
 *  `data-bt-feature="<id>"`, and add a row to the Display Settings
 *  page (via FEATURE_SECTIONS). */
export const FEATURE_IDS = [
  // ── Top bar — individual buttons (ExtensionTopButtonBar) ───────
  'topBtnInitError',
  'topBtnLogo',
  'topBtnOpenMessages',
  'topBtnLaunchCampaign',
  'topBtnOpenChat',
  'topBtnToggleLabels',
  'topBtnManageCampaigns',
  'topBtnReminders',
  'topBtnDebugDiag',
  // ── Top bar — right-side status indicators ─────────────────────
  'subscriptionBadge',
  'connectedEngines',
  'botStatus',
  'updateBadge',
  // ── Labels row (the secondary top bar with chips) ─────────────
  'labelFilterRow',
  // Master switch for the per-chat label chips that appear inside
  // each chat row's hover-action group (LabelsManagerButton).
  'chatRowLabels',
  // ── Chats list — per-row hover action chips ───────────────────
  'chatHoverInbox',
  'chatHoverNeedAttention',
  'chatHoverHandleLater',
  'chatHoverStar',
  // ── Conversation header (TasksActionBar) — per button ─────────
  'convChatHeaderActions',
  'convExportParticipants',
  'convToggleTasks',
  'convToggleMessages',
  'convNotesButton',
  'convChatAgent',
  'convAiConversation',
  'convChatReminder',
  // ── Per-message buttons (Note/Task/Reminder/Transcribe) ───────
  'msgNoteButton',
  'msgTaskButton',
  'msgReminderButton',
  'msgTranscribeButton',
  'msgTranscribeInlineButton',
  'msgTranslateButton',
  'msgMessageDetails',
  // Restore-deleted chip on revoked-message placeholders. Driven
  // by the message-vault (local IndexedDB backup of incoming
  // messages). Off by default — opt-in feature.
  'msgRestoreDeleted',
  // ── Side rail (floating column with Scheduled/Tasks/Notes/Rem) ─
  'sideRailScheduled',
  'sideRailTasks',
  'sideRailNotes',
  'sideRailReminders',
  // ── Composer (next to message input) ──────────────────────────
  'composerScheduleMessage',
  'composerAiAssistant',
] as const;

export type FeatureId = (typeof FEATURE_IDS)[number];

/** Features whose Display Settings toggle should only appear when the
 *  user is in admin / debug mode (`localStorage['bt-debug'] === 'true'`).
 *  Non-admin users won't see these rows in the panel and the wrappers
 *  in the DOM stay visible regardless of the persisted flag. */
export const ADMIN_ONLY_FEATURE_IDS: readonly FeatureId[] = [
  'topBtnDebugDiag',
  'msgMessageDetails',
];

/** Read the admin flag from localStorage. Safe to call in non-browser
 *  contexts; defaults to `false` if anything goes wrong. */
export const isAdminUser = (): boolean => {
  try {
    return (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('bt-debug') === 'true'
    );
  } catch {
    return false;
  }
};

/** Section grouping for the Display Settings UI. Each section has a
 *  master toggle that flips every child in one tap. */
export const FEATURE_SECTIONS: Record<string, { titleKey: string; titleFallback: string; ids: FeatureId[] }> = {
  topBarButtons: {
    titleKey: 'blueticksPanel.displaySettings.sections.topBarButtons',
    titleFallback: 'Top Bar Buttons',
    ids: [
      'topBtnInitError',
      'topBtnLogo',
      'topBtnOpenMessages',
      'topBtnLaunchCampaign',
      'topBtnOpenChat',
      'topBtnToggleLabels',
      'topBtnManageCampaigns',
      'topBtnReminders',
      'topBtnDebugDiag',
    ],
  },
  topBarStatus: {
    titleKey: 'blueticksPanel.displaySettings.sections.topBarStatus',
    titleFallback: 'Top Bar Status',
    ids: [
      'subscriptionBadge',
      'connectedEngines',
      'botStatus',
      'updateBadge',
    ],
  },
  labelsRow: {
    titleKey: 'blueticksPanel.displaySettings.sections.labelsRow',
    titleFallback: 'Labels Row',
    // Order set by user UX request — the two header-level rows
    // (labelFilterRow + the conv-header label-actions group) come
    // first, then chat-row label chip, then the chat-hover icons in
    // priority order (Star, Handle later, Needs attention, Inbox).
    ids: [
      'labelFilterRow',
      'convChatHeaderActions',
      'chatRowLabels',
      'chatHoverStar',
      'chatHoverHandleLater',
      'chatHoverNeedAttention',
      'chatHoverInbox',
    ],
  },
  conversation: {
    titleKey: 'blueticksPanel.displaySettings.sections.conversation',
    titleFallback: 'Conversation Header',
    ids: [
      'convExportParticipants',
      'convToggleTasks',
      'convToggleMessages',
      'convNotesButton',
      'convChatAgent',
      'convAiConversation',
      'convChatReminder',
    ],
  },
  perMessage: {
    titleKey: 'blueticksPanel.displaySettings.sections.perMessage',
    titleFallback: 'Per-Message Buttons',
    // msgTranscribeButton is intentionally NOT listed here. The
    // feature stays registered in FEATURE_IDS so the injection still
    // mounts the Transcribe button next to voice notes with its
    // default-true state, but no row appears in the Display Settings
    // UI. Removing the row keeps the user from toggling it while the
    // transcription pipeline is still being polished. Re-add this id
    // to surface the toggle again.
    ids: [
      'msgNoteButton',
      'msgTaskButton',
      'msgReminderButton',
      'msgTranscribeInlineButton',
      'msgTranslateButton',
      'msgMessageDetails',
      'msgRestoreDeleted',
    ],
  },
  sideRail: {
    titleKey: 'blueticksPanel.displaySettings.sections.sideRail',
    titleFallback: 'Side Rail',
    ids: [
      'sideRailScheduled',
      'sideRailTasks',
      'sideRailNotes',
      'sideRailReminders',
    ],
  },
  composer: {
    titleKey: 'blueticksPanel.displaySettings.sections.composer',
    titleFallback: 'Composer',
    ids: [
      'composerScheduleMessage',
      'composerAiAssistant',
    ],
  },
};

export type DisplaySettingsState = Record<FeatureId, boolean>;

const defaults: DisplaySettingsState = FEATURE_IDS.reduce(
  (acc, id) => {
    acc[id] = true;
    return acc;
  },
  {} as DisplaySettingsState,
);

const hydrate = (): DisplaySettingsState => {
  try {
    const raw =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(STORAGE_KEY)
        : null;
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw) as Partial<DisplaySettingsState>;
    return { ...defaults, ...parsed };
  } catch {
    return { ...defaults };
  }
};

const persist = (state: DisplaySettingsState) => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.warn('[display-settings] persist failed', err);
  }
};

const displaySettingsSlice = createSlice({
  name: 'DisplaySettings',
  initialState: hydrate(),
  reducers: {
    setFeatureVisible(
      state,
      action: PayloadAction<{ id: FeatureId; visible: boolean }>,
    ) {
      state[action.payload.id] = action.payload.visible;
      persist(state);
    },
    toggleFeature(state, action: PayloadAction<FeatureId>) {
      state[action.payload] = !state[action.payload];
      persist(state);
    },
    setSectionVisible(
      state,
      action: PayloadAction<{ ids: FeatureId[]; visible: boolean }>,
    ) {
      for (const id of action.payload.ids) {
        state[id] = action.payload.visible;
      }
      persist(state);
    },
    resetDisplaySettings(state) {
      for (const id of FEATURE_IDS) {
        state[id] = true;
      }
      persist(state);
    },
  },
});

export const {
  setFeatureVisible,
  toggleFeature,
  setSectionVisible,
  resetDisplaySettings,
} = displaySettingsSlice.actions;

export default displaySettingsSlice.reducer;
